import React from 'react';
import styled from 'styled-components';
import { Button, Icon, Modal } from '@repo/design-system';
import { glass, radius, semantic, space, typeRole } from '@repo/design-tokens';

export interface LinkReplaceModalProps {
  /** *Replace {old} with {new}?* */
  title: string;
  /** What moves, in two or three sentences (`replaceSentences`). */
  body: string;
  /** The camera, its floor and its coverage: *{camera} · {floor} · {coverage}*. */
  row: string;
  /** The stream the camera shows now — the Keep button names it. */
  oldName: string;
  onReplace: () => void;
  onKeep: () => void;
  /** Where focus goes when it closes: the camera's marker, else the plan. */
  returnFocusTo?: { readonly current: HTMLElement | null };
}

/**
 * Frame 08's confirm, when a stream is dropped on a camera that already shows another
 * (`camera-feeds-page-link`, spec R7): a butter Warning modal at 440 that names both streams and
 * what happens to each. Replace is the primary action — nothing is lost, the old stream goes back to
 * Not placed — but focus opens on *Keep {old}*, the choice that changes nothing.
 *
 * The camera row is page-local, built from tokens, as the docked panel's *No stream yet* box is.
 */
const LinkReplaceModal: React.FC<LinkReplaceModalProps> = ({
  title,
  body,
  row,
  oldName,
  onReplace,
  onKeep,
  returnFocusTo,
}) => (
  <Modal
    open
    onClose={onKeep}
    returnFocusTo={returnFocusTo}
    title={title}
    tone="butter"
    width="md"
    icon={<Icon name="arrow-right-arrow-left" />}
    actions={
      <>
        <Button type="button" variant="frosted" size="inline" autoFocus onClick={onKeep}>
          Keep {oldName}
        </Button>
        <Button
          type="button"
          variant="primary"
          size="inline"
          iconLeft={<Icon name="arrow-right-arrow-left" />}
          onClick={onReplace}
        >
          Replace
        </Button>
      </>
    }
  >
    <Body>
      <p>{body}</p>
      <CameraRow data-testid="link-replace-camera">
        <Icon name="video" size="sm" />
        <span>{row}</span>
      </CameraRow>
    </Body>
  </Modal>
);

export default LinkReplaceModal;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${space[3]};

  p {
    margin: 0;
    font-size: ${typeRole.body1Strong.fontSize};
    line-height: ${typeRole.body1Strong.lineHeight};
    font-weight: ${typeRole.body1Strong.fontWeight};
    color: ${semantic.text.primary};
  }
`;

/* Frame 08's camera row (T:1679): a frosted strip with the video glyph and the camera's line. */
const CameraRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${space[2]};
  padding: ${space[2]} ${space[3]};
  border-radius: ${radius.md};
  background: ${glass.bg};
  border: 1px solid ${glass.border};
  font-size: ${typeRole.caption1Strong.fontSize};
  line-height: ${typeRole.caption1Strong.lineHeight};
  font-weight: ${typeRole.caption1Strong.fontWeight};
  color: ${semantic.text.primary};

  svg {
    color: ${semantic.text.body};
  }
`;
